import paginationTemplate from '@naturehouse/design-system/components/molecules/pagination/Pagination.html.njk';
import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import PaginationResults from '../../../enums/pagination';
import calculatePagination from '../../../util/pagination';
import HouseSearchListItems from '../store/HouseSearchListItems';

export class PaginationRenderer implements Observer {
    readonly #searchListItems: HouseSearchListItems;

    readonly #template: NunjucksTemplate;

    readonly #paginationElement: HTMLElement | null;

    readonly #translations: Record<string, string>;

    constructor(
        searchListItems: HouseSearchListItems,
        template: NunjucksTemplate,
        paginationElement: HTMLElement
    ) {
        this.#searchListItems = searchListItems;
        this.#template = template;
        this.#paginationElement = paginationElement;

        this.#translations = JSON.parse(paginationElement?.dataset.translations || '{}');

        this.#searchListItems.attach(this);
    }

    public update(): void {
        if (!this.#paginationElement) {
            return;
        }

        if (this.#searchListItems.total === 0) {
            this.#paginationElement.innerHTML = '';
            return;
        }

        this.#paginationElement.innerHTML = this.#createTemplate().innerHTML;
    }

    #createTemplate(): HTMLElement {
        const url: URL = new URL(window.location.href);

        const pagination = calculatePagination(
            url.searchParams.get('skip') ? Number(url.searchParams.get('skip')) : 0,
            url.searchParams.get('limit')
                ? Number(url.searchParams.get('limit'))
                : PaginationResults.DEFAULT,
            this.#searchListItems.total,
            (skip: number, limit: number) => {
                url.searchParams.set('skip', skip.toString());
                url.searchParams.set('limit', limit.toString());
                return url.href;
            }
        );

        const pages = Array.from(pagination.pages as Map<string, string>).map((page) => ({
            label: page[0],
            href: page[1]
        }));

        const html = paginationTemplate.render({
            labels: {
                of: this.#translations.of
            },
            current: pagination.current,
            total: pagination.total,
            pages: pages,
            previous: {
                label: this.#translations.previous,
                href: pagination.prev
            },
            next: {
                label: this.#translations.next,
                href: pagination.next
            },
            first: {
                label: '1',
                href: pagination.first
            },
            last: {
                label: pagination.total,
                href: pagination.last
            },
            data: {
                'data-role': this.#paginationElement?.dataset.role,
                'data-cy': this.#paginationElement?.dataset.cy,
                'data-translations': this.#paginationElement?.dataset.translations
            }
        });

        return parseStringAsHtml(html, 'nav');
    }
}

export default new PaginationRenderer(
    HouseSearchListItems.getInstance(),
    paginationTemplate,
    document.querySelector('[data-role="pagination"]') as HTMLElement
);
